import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getDataQualityReport, DataQualityIssue } from "../services/api";

const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "8px 18px", cursor: "pointer", fontSize: "13px",
};

const CATEGORIES: { key: keyof DataQualityReportIssues; title: string; description: string }[] = [
    { key: "po_number", title: "Missing / Pending PO#", description: "No PO# on file, or PO# is still \"pending\"." },
    { key: "pm", title: "No PM / Approver", description: "Zoho's PM name doesn't match any user, and no manual override is set in User Management." },
    { key: "budget", title: "No Budget Entered", description: "No budget amount on file in Zoho." },
    { key: "rate", title: "Missing Billing Rate", description: "A user assignment (Staff Hours) or task (Task Hours) has no rate set in Zoho -- that portion of Actuals will show $0.00." },
    { key: "zero_actuals", title: "Billing Setup Likely to Produce $0 Actuals", description: "Task Hours project with 0 active tasks, or Staff Hours project with 0 assigned users -- Actuals will always show $0.00 until this is fixed in Zoho." },
    { key: "stale_sync", title: "Stale Zoho Sync", description: "This project hasn't synced from Zoho in over a week -- the nightly sync may be failing for it specifically." },
    { key: "billing_type", title: "Unrecognized Billing Type", description: "Zoho's billing type for this project doesn't match any known T&M or Fixed Price value, so its financials won't compute correctly." },
];

interface DataQualityReportIssues {
    po_number: DataQualityIssue[];
    pm: DataQualityIssue[];
    budget: DataQualityIssue[];
    rate: DataQualityIssue[];
    zero_actuals: DataQualityIssue[];
    stale_sync: DataQualityIssue[];
    billing_type: DataQualityIssue[];
}

const emptyIssues: DataQualityReportIssues = {
    po_number: [], pm: [], budget: [], rate: [], zero_actuals: [], stale_sync: [], billing_type: [],
};

const DataQualityReport: React.FC = () => {
    const navigate = useNavigate();
    const [issues, setIssues] = useState<DataQualityReportIssues>(emptyIssues);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        setLoading(true);
        try {
            const data = await getDataQualityReport();
            setIssues(data.issues as DataQualityReportIssues);
        } catch (e) {
            console.error("Failed to load Data Quality Report", e);
            setError("Failed to load Data Quality Report. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totalIssues = CATEGORIES.reduce((sum, c) => sum + issues[c.key].length, 0);

    if (loading) return <div>Loading Data Quality Report...</div>;

    if (error) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{error}</div>
                <button onClick={load} style={secondaryBtnStyle}>Retry</button>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                <h1 style={{ color: "#5C5D5E", margin: 0, fontSize: "22px" }}>Data Quality Report</h1>
                <button onClick={load} style={secondaryBtnStyle}>Refresh</button>
            </div>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "24px", fontSize: "13px" }}>
                Every active project checked against Zoho/local data for common gaps. Reads from the last Zoho sync
                and Project Info refresh -- click Refresh above to re-run against current data.
                {totalIssues === 0
                    ? " No issues found."
                    : ` ${totalIssues} issue${totalIssues !== 1 ? "s" : ""} found across ${CATEGORIES.filter(c => issues[c.key].length > 0).length} categor${CATEGORIES.filter(c => issues[c.key].length > 0).length !== 1 ? "ies" : "y"}.`}
            </p>

            {CATEGORIES.map(cat => {
                const rows = issues[cat.key];
                if (rows.length === 0) return null;
                return (
                    <div key={cat.key} style={{ marginBottom: "28px" }}>
                        <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 4px 0" }}>
                            {cat.title} <span style={{ color: "#999", fontWeight: "normal" }}>({rows.length})</span>
                        </h2>
                        <p style={{ color: "#999", fontSize: "12px", margin: "0 0 10px 0" }}>{cat.description}</p>
                        <div style={{ border: "1px solid #e0e0e0", borderRadius: "6px", backgroundColor: "white" }}>
                            {rows.map((r, idx) => (
                                <div
                                    key={`${r.project_id}-${idx}`}
                                    onClick={() => navigate(`/reports/project-info/${r.project_id}`)}
                                    style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "10px 14px", fontSize: "13px", cursor: "pointer",
                                        borderBottom: idx < rows.length - 1 ? "1px solid #f0f0f0" : "none",
                                    }}
                                >
                                    <span style={{ color: "#5C5D5E", fontWeight: "bold" }}>{r.project_name}</span>
                                    <span style={{ color: "#666" }}>{r.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            {totalIssues === 0 && (
                <div style={{ color: "#999", fontSize: "13px" }}>No data-quality issues found across active projects.</div>
            )}
        </div>
    );
};

export default DataQualityReport;
